// ----------------------------------------------------------------
// <copyright company='Microsoft Corporation'>
//     Copyright (c) Microsoft Corporation.  All rights reserved.
// </copyright>
// ----------------------------------------------------------------

import clsx from 'clsx';
import { saveLoadFactory, SaveLocation } from 'application-storage';
import moment from 'moment';
import { DefaultButton } from 'office-ui-fabric-react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import {
    getLineItemName,
    getShippedSerialNumber,
} from './../../components/LineItem';
import URLS from './../../constants/urls';
import { logPageAction } from './../../lib/ApplicationInsights';
import { IClaimOrder, ILineItem } from './../../businessLogic/sagas/serviceRequest';
import { ClaimOrderLineItemAttributes } from './../../store/orderCreation';

import Styles from './printReceipt.module.scss';

function Body() {
    const { t } = useTranslation();
    const dataKey = 'claim';
    const appStorage = saveLoadFactory(dataKey, SaveLocation.LocalStorage);
    const data : IClaimOrder = appStorage.getValue();
    appStorage.removeValue();
    
    if (!data) {
        return (
            <div>{t('error_occurred')}</div>
        );
    }

    return (
        <div className={Styles.body}>
            <div className={Styles.title}>{t('order_receipt')}</div>
            <section className={Styles.section}>
                <BodyLeft data={data} />
                <BodyRight data={data} />
            </section>
        </div>
    );
}

function BodyLeft({ data }: { data: IClaimOrder }) {
    const { t } = useTranslation();

    const {
        lineItems,
        payments,
        serviceRequests,
        shipments,
    } = data;

    const ServiceRequestIds = serviceRequests.map(request => request.serviceRequestNumber !== 0 && request.serviceRequestNumber)
                                             .filter(request => request !== false)
                                             .join(',');

    const {
        billTo,
    } = payments;
    
    const {
        shipTo,
    } = shipments;

    // show shipping info if at least one line item needs to be shipped
    const showShipping = lineItems.some((lineItem: ILineItem) => {
        const isShipping = lineItem.itemAttributes !== ClaimOrderLineItemAttributes.InStore
            && lineItem.itemAttributes !== ClaimOrderLineItemAttributes.InStorePart;
        return isShipping;
    });

    return (
        <div className={Styles.bodyLeft}>
            <RenderDetail label={t('claim_order_number')} value={data.claimOrderId}/>
            <RenderDetail label={t('service_plan')} value={`${t('sr')}# ${ServiceRequestIds}`}/>
            <RenderLineItemsInfo lineItems={lineItems} />
            <RenderDetail label={t('customer_contact_info')}>
                <div>{`${billTo.firstName || ''} ${billTo.lastName || ''}`}</div>
            </RenderDetail>
            {
                showShipping &&
                <>
                    <RenderDetail label={t('ship_to_address')}>
                        <div>
                            <div className={Styles.address}>{shipTo.addressLine1}</div>
                            <div className={Styles.address}>{shipTo.addressLine2}</div>
                            <div className={Styles.address}>{shipTo.addressLine3}</div>
                            <div className={Styles.city}>{shipTo.city}</div>
                            <div className={Styles.state}>{shipTo.region}</div>
                            <div className={Styles.country}>{shipTo.country}</div>
                            <div className={Styles.postalCode}>{shipTo.postalCode}</div>
                            <div className={Styles.email}>{shipTo.emailId}</div>
                            <div className={Styles.phone}>{shipTo.phoneNumber}</div>
                        </div>
                    </RenderDetail>
                    <RenderDetail label={t('bill_to_address')}>
                        <div>
                            <div className={Styles.address}>{billTo.addressLine1}</div>
                            <div className={Styles.address}>{billTo.addressLine2}</div>
                            <div className={Styles.address}>{billTo.addressLine3}</div>
                            <div className={Styles.city}>{billTo.city}</div>
                            <div className={Styles.state}>{billTo.region}</div>
                            <div className={Styles.country}>{billTo.country}</div>
                            <div className={Styles.postalCode}>{billTo.postalCode}</div>
                            <div className={Styles.email}>{billTo.emailId}</div>
                            <div className={Styles.phone}>{billTo.phoneNumber}</div>
                        </div>
                    </RenderDetail>
                </>
            }
        </div>
    );
}

function BodyRight({ data }: { data: IClaimOrder }) {
    const { t } = useTranslation();
    const {
        lineItems,
        payments,
    } = data;
    const lineItem = lineItems[0];
    const {
        deviceInfo,
    } = lineItem;

    const {
        currency,
        totalItemsTaxAmount,
        totalShippingAmount,
        totalAmount,
    } = payments;

    return (
        <div className={Styles.bodyRight}>
            <RenderRow
                className={Styles.rowSpacing}
                label={productImage(deviceInfo.imageUri)}
                value={RenderNameAndWarranty(lineItem)}
            />
            {
                lineItems.map((lineItem: ILineItem, key: number) => {
                    const {
                        totalAmount,
                        shippingOffer,
                    } = lineItem;

                    return (
                        <div className={Styles.rowSpacing} key={key}>
                            <RenderRow
                                label={lineItem.repairOffer?.name ? getLineItemName(lineItem, true) : getLineItemName(lineItem, false)}
                                value={<RenderPrice cost={totalAmount} unit={currency}/>}
                            />
                            <RenderRow
                                label={shippingOffer.name}
                                value={<RenderPrice cost={0} unit={currency}/>}
                            />
                        </div>
                    );
                })
            }
            <RenderRow
                className={Styles.rowSpacing}
                label={t('tax')}
                value={<RenderPrice cost={totalItemsTaxAmount} unit={currency}/>}
            />
            <RenderRow
                className={Styles.rowSpacing}
                label={t('shipping')}
                value={<RenderPrice cost={totalShippingAmount} unit={currency}/>}
            />
            <Divider />
            <RenderRow
                className={Styles.rowSpacing}
                label={t('order_total')}
                value={<RenderPrice cost={totalAmount} unit={currency}/>}
            />
        </div>
    );
}

function Divider () {
    return <div className={Styles.divider} />;
}

function Header() {
    const { t } = useTranslation();

    const privacyStatementUrl = URLS.privacy_statement;

    const onClose = () => {
        logPageAction('Close button clicked');

        window.close();
    };

    const onPrint = () => {
        logPageAction('Print receipt button clicked');

        window.print();
    };

    return (
        <header className={Styles.header}>
            <div className={Styles.topHeader}>
                <DefaultButton
                    className={Styles.closeButton}
                    text={t('close')}
                    onClick={onClose}
                />
                <DefaultButton
                    className={Styles.printButton}
                    text={t('print')}
                    onClick={onPrint}
                />
            </div>
            <div className={Styles.bottomHeader}>
                {t('review_microsoft')}&nbsp;
                <a href={privacyStatementUrl} target="_blank" rel="noreferrer noopener">
                    {t('privacy_statement')}
                </a>
            </div>
        </header>
    );
}

function productImage(src: string) {
    return (
      <img
        alt="Product Image"
        className={Styles.image}
        src={src}
      />
    );
}

export default function PrintReceipt () {
    return (
        <div className={Styles.main}>
            <Header />
            <Body />
        </div>
    );
}

function RenderDetail({ label, value, children, noMargin }: any) {
    const className = clsx({
        [Styles.detail]: !noMargin,
    });
    return (
        <div className={className}>
            <div className={Styles.label}>
                {label}:
                <span className={Styles.value}>&nbsp;{value}</span>
            </div>
            {children}
        </div>
    );
}

function RenderLineItemsInfo({ lineItems }: { lineItems: ILineItem[] }) {
    const { t } = useTranslation();
    return (
        <>
            {lineItems.map((lineItem: ILineItem, key: number) => {
                const {
                    replacementSerialNumber,
                    serialNumber,
                    progress,
                } = lineItem || {};

                const {
                    states = [],
                } = progress || {};

                const replaceOrShippedSerial = replacementSerialNumber || getShippedSerialNumber(states);

                const label = t('name');
                const name = getLineItemName(lineItem);
                return (
                    <div className={Styles.marginBottom10} key={key}>
                        <RenderDetail
                            label={label}
                            noMargin={true}
                            value={name}
                        />
                        <RenderDetail
                            label={t('product_serial_number')}
                            noMargin={true}
                            value={serialNumber}
                        />
                        {replaceOrShippedSerial && <RenderDetail
                            label={t('replacement_serial_number')}
                            noMargin={true}
                            value={replaceOrShippedSerial}
                        />}
                    </div>
                );
            })}
        </>
    );
}

function RenderNameAndWarranty(lineItem: ILineItem) {
    const { t } = useTranslation();

    const dataKey = 'warranties';
    const appStorage = saveLoadFactory(dataKey, SaveLocation.LocalStorage);
    const data = appStorage.getValue();
    appStorage.removeValue();

    let warrantyEndDate = '';

    if (data) {
        const warranty = data.find((w: any) => w.entitlementId.toUpperCase() === 'MANUFACTURERWARRANTY');
    
        const endDate = moment(warranty.endDate).format('ll');

        warrantyEndDate = `${t('standard_warranty')}: ${t('expires')} ${endDate}`;
    }

    return (
        <div>
            <div>{lineItem.deviceInfo.localizedName}</div>
            <div>{warrantyEndDate}</div>
        </div>
    );
}

function RenderPrice({ cost, unit }: any) {
    return (
        <div className={Styles.price}>
            <div>{cost}</div>
            <div className={Styles.priceUnit}>{unit}</div>
        </div>
    );
}

function RenderRow({ className, label, value, key }: any) {
    return (
        <div className={clsx(Styles.row, className)} key={key}>
            <div className={Styles.columnLeft}>{label}</div>
            <div className={Styles.columnRight}>{value}</div>
        </div>
    );
}